import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Send } from "lucide-react";
import { useScrollReveal, useScrollToSuccess } from "../lib/hooks";
import {
  PHONE, PHONE_LINK, SERVICE_OPTIONS, GOOGLE_REVIEW_URL, FONT_HEAD, FONT_BODY, NAVY,
} from "../lib/constants";
import { SectionBadge } from "./Shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INPUT_STYLE = {
  fontFamily: FONT_BODY,
  color: "#2D2D2D",
  border: "1px solid rgba(27,58,107,0.22)",
  borderRadius: "2px",
  background: "#FFFFFF",
  fontSize: "14px",
};

const LABEL_STYLE = { color: "#2D2D2D", fontFamily: FONT_BODY };

const Field = ({ label, optional, children }) => (
  <div>
    <label className="block text-sm font-semibold mb-2" style={LABEL_STYLE}>
      {label}{" "}
      {optional && <span className="font-normal text-[#6B7280]">(optional)</span>}
    </label>
    {children}
  </div>
);

const ContactMethodPicker = ({ value, onChange }) => (
  <div>
    <label className="block text-sm font-semibold mb-3" style={LABEL_STYLE}>
      Preferred Contact Method
    </label>
    <div className="flex flex-wrap gap-5">
      {[
        { value: "call", label: "Call me" },
        { value: "text", label: "Text me" },
        { value: "email", label: "Email me" },
      ].map((opt) => (
        <label key={opt.value} data-testid={`contact-method-${opt.value}`} className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="contactMethod"
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="sr-only"
          />
          <div
            className="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200"
            style={{
              borderColor: value === opt.value ? NAVY : "rgba(27,58,107,0.3)",
              background: value === opt.value ? NAVY : "transparent",
            }}
          >
            {value === opt.value && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
          <span className="text-sm font-medium" style={LABEL_STYLE}>
            {opt.label}
          </span>
        </label>
      ))}
    </div>
  </div>
);

const SuccessMessage = ({ name }) => (
  <div className="max-w-xl mx-auto px-4 md:px-8 text-center">
    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(27,58,107,0.1)" }}>
      <CheckCircle size={40} style={{ color: NAVY }} />
    </div>
    <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: FONT_HEAD, color: NAVY }} data-testid="success-message">
      Quote Request Received!
    </h2>
    <p className="text-base mb-6 leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      Thank you, <strong>{name}</strong>! We've received your request and will
      contact you shortly via your preferred method.
    </p>
    <p className="text-sm mb-6" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      Need immediate assistance? Call us at{" "}
      <a href={PHONE_LINK} className="font-bold" style={{ color: NAVY }}>
        {PHONE}
      </a>
    </p>
    <a
      href={GOOGLE_REVIEW_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="quote-success-review-link"
      className="inline-flex items-center gap-2 px-5 py-3 text-sm font-bold transition-all duration-200 hover:opacity-90"
      style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
    >
      ★ Enjoyed our service? Leave us a Google Review
    </a>
  </div>
);

/* Inputs are uncontrolled on purpose: the page is pre-rendered (SSG), so text
   typed before React hydrates must survive. Values are read via FormData. */
const readQuotePayload = (form, contactMethod) => {
  const fd = new FormData(form);
  const get = (k) => (fd.get(k) || "").toString().trim();
  const payload = {
    fullName: get("fullName"),
    phone: get("phone"),
    email: get("email"),
    service: (fd.get("service") || "").toString(),
    message: get("message"),
    contactMethod,
  };
  const valid = payload.fullName && payload.phone && payload.email && payload.service;
  return valid ? payload : null;
};

const useQuoteForm = () => {
  const [contactMethod, setContactMethod] = useState("call");
  const [loading, setLoading] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = readQuotePayload(e.target, contactMethod);
    if (!payload) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/quote`, payload);
      setSubmittedName(payload.fullName);
    } catch {
      setError("Something went wrong. Please try again or call us directly at " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  return { contactMethod, setContactMethod, loading, submittedName, error, handleSubmit };
};

const TextInput = ({ label, name, type, placeholder }) => (
  <Field label={label}>
    <input
      type={type}
      name={name}
      defaultValue=""
      placeholder={placeholder}
      data-testid={`input-${name === "fullName" ? "full-name" : name}`}
      className="w-full px-4 py-3"
      style={INPUT_STYLE}
      required
    />
  </Field>
);

const ServiceSelect = () => (
  <Field label="Service Needed *">
    <select
      name="service"
      defaultValue=""
      data-testid="select-service"
      className="w-full px-4 py-3"
      style={INPUT_STYLE}
      required
    >
      <option value="">Select a service...</option>
      {SERVICE_OPTIONS.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  </Field>
);

const QuoteFormFields = () => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <TextInput label="Full Name *" name="fullName" type="text" placeholder="Jane Smith" />
      <TextInput label="Phone Number *" name="phone" type="tel" placeholder="(239) 000-0000" />
    </div>
    <TextInput label="Email Address *" name="email" type="email" placeholder="jane@example.com" />
    <ServiceSelect />
  </>
);

const FormError = ({ error }) =>
  error ? (
    <div
      data-testid="form-error"
      className="px-4 py-3 text-sm"
      style={{
        background: "rgba(239,68,68,0.07)",
        color: "#DC2626",
        border: "1px solid rgba(239,68,68,0.2)",
        fontFamily: FONT_BODY,
      }}
    >
      {error}
    </div>
  ) : null;

const SubmitButton = ({ loading, ready }) => (
  <button
    type="submit"
    data-testid="submit-quote-btn"
    disabled={loading || !ready}
    className="w-full py-4 text-base font-bold tracking-wide transition-all duration-300 hover:opacity-90 hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
    style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
  >
    {loading ? (
      "Sending..."
    ) : (
      <>
        <Send size={18} />
        Request My Free Quote
      </>
    )}
  </button>
);

const FormHeader = () => (
  <div className="text-center mb-12">
    <SectionBadge>Get Started</SectionBadge>
    <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      Get Your Free Quote Today
    </h2>
    <p className="mt-4 text-base" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      No commitment. Just a clean space you'll love.
    </p>
  </div>
);

const QuoteFormBody = ({ contactMethod, setContactMethod, handleSubmit, error, loading, ready }) => (
  <form onSubmit={handleSubmit} data-testid="quote-form" className="space-y-6">
    <QuoteFormFields />
    <ContactMethodPicker value={contactMethod} onChange={setContactMethod} />
    <Field label="Message" optional>
      <textarea
        name="message"
        defaultValue=""
        placeholder="Tell us about your space, frequency needs, or any special requirements..."
        data-testid="input-message"
        rows={4}
        className="w-full px-4 py-3 resize-none"
        style={INPUT_STYLE}
      />
    </Field>
    <FormError error={error} />
    <SubmitButton loading={loading} ready={ready} />
  </form>
);

export const QuoteFormSection = () => {
  const ref = useScrollReveal();
  const { contactMethod, setContactMethod, loading, submittedName, error, handleSubmit } = useQuoteForm();
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), [setReady]);
  useScrollToSuccess(!!submittedName, '[data-testid="quote-form-section"]');

  return (
    <section
      id="quote-form"
      data-testid="quote-form-section"
      ref={ref}
      className="py-20 md:py-28 section-hidden"
      style={{ background: "#FFFFFF" }}
    >
      {submittedName ? (
        <SuccessMessage name={submittedName} />
      ) : (
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <FormHeader />
          <QuoteFormBody
            contactMethod={contactMethod}
            setContactMethod={setContactMethod}
            handleSubmit={handleSubmit}
            error={error}
            loading={loading}
            ready={ready}
          />
        </div>
      )}
    </section>
  );
};
